import { TachographCompanyCard, TachographDataFile } from "@fairfleet/geotab";

/** The tabs of the tachograph page, in display order. */
export const TACHOGRAPH_TABS = [
  "status",
  "driver",
  "downloads",
  "inspections",
  "companyCards",
  "settings",
  "rcsList",
  "connectors",
] as const;

export type TachographTab = (typeof TACHOGRAPH_TABS)[number];

export type TachographStatus = "ok" | "warning" | "overdue";

/** What the counters above the table narrow the vehicle list down to. */
export type TachographSummaryFilter = "all" | "downloads" | "inspections" | "connectors";

export type TachographConnectivity = "online" | "degraded" | "offline";

export type CompanyCardStatus = "available" | "busy" | "offline";

/** The makes the tachograph itself reports. */
export type TachographManufacturer =
  | "notAvailable"
  | "unknown"
  | "vdo"
  | "stoneridge"
  | "actia"
  | "efas";

/** What the tachograph of a vehicle says about itself. */
export type TachographDeviceInfo = {
  manufacturer: TachographManufacturer | null;
  nextCalibration: Date | null;
};

/** A tachograph data file as the API returns it — any field may be missing. */
export type TachographFile = Partial<TachographDataFile>;

/** A company card as the API returns it — any field may be missing. */
export type CompanyCard = Partial<TachographCompanyCard>;

/** The download deadlines derived from the last file of a device or driver. */
export type TachographTimeline = {
  legalLimit: string;
  nextDownloadUtc: string;
  calibrationExpiryDate: string;
  updateTime: string;
};

export type TachographRow = {
  id: string;
  summary: string;
  driver: string;
  licensePlate: string;
  driverId?: string;
  manufacturer: string;
  legalLimit: string;
  nextDownloadUtc: string;
  calibrationExpiryDate: string;
  updateTime: string;
  status: TachographStatus;
  downloadStatus: TachographStatus;
  inspectionStatus: TachographStatus;
  companyCardStatus: TachographStatus;
  connectorStatus: TachographConnectivity;
  category: TachographTab;
};

export type TachographDriverRow = {
  id: string;
  driver: string;
  driverCardNumber: string;
  licensePlate: string;
  legalLimit: string;
  nextDownloadUtc: string;
  calibrationExpiryDate: string;
  updateTime: string;
};

export type TachographDownloadRow = {
  id: string;
  source: string;
  fileName: string;
  unit: string;
  dataFromUtc: string;
  dataToUtc: string;
  createdAt: string;
  addedAt: string;
};

export type TachographCompanyCardRow = {
  id: string;
  cardNumber: string;
  companyName: string;
  icc: string;
  status: string;
  source: string;
  validFrom: string;
  expiryDateUtc: string;
  lastUpdate: string;
};

/** How often a driver card and a vehicle unit have to be read out. */
export type TachographPolicy = {
  driverIntervalDays: number;
  deviceIntervalDays: number;
  source: "geotab" | "fallback";
};

/** Name and license plate of the vehicle a file belongs to. */
export type TachographUnitLabel = {
  name: string;
  licensePlate: string;
};
